import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { blogAPI, contactAPI } from "../services/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Loader2, Mail, Users, Calendar, Download, Plus, Eye, EyeOff, FileText, BarChart3, TrendingUp, Activity } from "lucide-react";

const AdminPage = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [blogStats, setBlogStats] = useState({
    total: 0,
    public: 0,
    internal: 0,
    lastPublicDate: null
  });
  const [recentInternalBlogs, setRecentInternalBlogs] = useState([]);
  const [homepageBlogs, setHomepageBlogs] = useState([]);
  const [selectedHomepageBlogs, setSelectedHomepageBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [requestsResponse, blogsResponse] = await Promise.all([
        contactAPI.getRequests(),
        blogAPI.getBlogs({ includeInternal: true, limit: 100 })
      ]);

      if (requestsResponse.success) {
        setRequests(requestsResponse.data);
      }
      if (blogsResponse.success) {
        const allBlogs = blogsResponse.data.blogs || blogsResponse.data || [];
        setBlogs(allBlogs);

        // Calculate blog statistics
        const publicBlogs = allBlogs.filter(blog => (blog.visibility || 'public') === 'public');
        const internalBlogs = allBlogs.filter(blog => blog.visibility === 'internal');

        // Get last public blog date
        const lastPublicBlog = publicBlogs
          .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))[0];

        setBlogStats({
          total: allBlogs.length,
          public: publicBlogs.length,
          internal: internalBlogs.length,
          lastPublicDate: lastPublicBlog ? lastPublicBlog.publishedAt : null
        });

        // Get recent internal blogs
        const recentInternal = internalBlogs
          .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
          .slice(0, 3);
        setRecentInternalBlogs(recentInternal);

        // Load homepage blog configuration
        const savedHomepageBlogs = JSON.parse(localStorage.getItem('myfinbrain_homepage_blogs') || '[]');
        setSelectedHomepageBlogs(savedHomepageBlogs);
        setHomepageBlogs(allBlogs); // All blogs available for selection
      }
    } catch (error) {
      console.error("Failed to load admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    try {
      const response = await contactAPI.exportRequests('csv');
      if (response.success) {
        const dataUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(response.data);
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', response.filename);
        linkElement.click();
      } else {
        alert('Failed to export requests: ' + response.error.message);
      }
    } catch (error) {
      alert('Failed to export requests: ' + error.message);
    }
  };



  const filteredRequests = activeTab === "all"
    ? requests
    : requests.filter(req => req.type === activeTab);

  const handleHomepageBlogToggle = (blogId) => {
    setSelectedHomepageBlogs(prev => {
      const isSelected = prev.includes(blogId);
      let newSelection;

      if (isSelected) {
        // Remove from selection
        newSelection = prev.filter(id => id !== blogId);
      } else {
        // Add to selection (max 3)
        if (prev.length < 3) {
          newSelection = [...prev, blogId];
        } else {
          // Replace the first one if already at max
          newSelection = [prev[1], prev[2], blogId];
        }
      }

      // Save to localStorage
      localStorage.setItem('myfinbrain_homepage_blogs', JSON.stringify(newSelection));
      return newSelection;
    });
  };

  const clearHomepageBlogs = () => {
    setSelectedHomepageBlogs([]);
    localStorage.setItem('myfinbrain_homepage_blogs', JSON.stringify([]));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
          <p className="text-slate-600">Manage your blog, requests, and analytics</p>
        </div>

        {/* Section 1: Blog Management */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Blog Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Blog Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-900">{blogStats.total}</div>
                    <div className="text-sm text-slate-600">Total Blogs</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{blogStats.public}</div>
                    <div className="text-sm text-slate-600">Public Blogs</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{blogStats.internal}</div>
                    <div className="text-sm text-slate-600">Internal Blogs</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-sm font-medium text-slate-900">
                      {blogStats.lastPublicDate || 'No public blogs'}
                    </div>
                    <div className="text-sm text-slate-600">Last Public Blog</div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mb-6">
              <Button
                onClick={() => navigate('/blog/create')}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Post
              </Button>
              <Button
                asChild
                variant="outline"
                className="flex items-center gap-2"
              >
                <Link to="/blog-internal">
                  <EyeOff className="w-4 h-4" />
                  Internal Blog
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="flex items-center gap-2"
              >
                <Link to="/blog">
                  <Eye className="w-4 h-4" />
                  Public Blog
                </Link>
              </Button>
            </div>

            {/* Recent Internal Blogs */}
            {recentInternalBlogs.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent Internal Blogs</h3>
                <div className="space-y-3">
                  {recentInternalBlogs.map((blog) => (
                    <Link
                      key={blog.id}
                      to={`/blog/${blog.id}`}
                      className="block hover:bg-slate-100 transition-colors duration-200 rounded-lg"
                    >
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <div>
                          <h4 className="font-medium text-slate-900 hover:text-blue-600 transition-colors duration-200">{blog.title}</h4>
                          <p className="text-sm text-slate-600">
                            {new Date(blog.publishedAt).toLocaleDateString()} • {blog.author} • {blog.readTime} min read
                          </p>
                        </div>
                        <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                          Internal
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Homepage Blog Configuration */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Homepage Blog Section</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">
                    {selectedHomepageBlogs.length}/3 selected
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearHomepageBlogs}
                    disabled={selectedHomepageBlogs.length === 0}
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              <p className="text-sm text-slate-600 mb-4">
                Select up to 3 blogs to display in the homepage /#blog section. Leave empty to hide the section.
              </p>

              {/* Selected Blogs */}
              {selectedHomepageBlogs.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-slate-900 mb-2">Selected for Homepage:</h4>
                  <div className="space-y-2">
                    {selectedHomepageBlogs.map((blogId, index) => {
                      const blog = homepageBlogs.find(b => b.id === blogId);
                      if (!blog) return null;
                      return (
                        <div key={blogId} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-blue-700">#{index + 1}</span>
                            <div>
                              <h5 className="font-medium text-slate-900">{blog.title}</h5>
                              <p className="text-sm text-slate-600">
                                {blog.tags && blog.tags.length > 0 ? blog.tags[0] : 'No tags'} • {new Date(blog.publishedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleHomepageBlogToggle(blogId)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Remove
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Available Blogs */}
              <div>
                <h4 className="text-sm font-medium text-slate-900 mb-2">Available Blogs:</h4>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {homepageBlogs
                    .filter(blog => !selectedHomepageBlogs.includes(blog.id))
                    .map((blog) => (
                      <div key={blog.id} className="flex items-center justify-between p-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            {blog.visibility === 'internal' ? (
                              <EyeOff className="w-4 h-4 text-orange-500" />
                            ) : (
                              <Eye className="w-4 h-4 text-green-500" />
                            )}
                          </div>
                          <div>
                            <h5 className="font-medium text-slate-900">{blog.title}</h5>
                            <p className="text-sm text-slate-600">
                              {blog.tags && blog.tags.length > 0 ? blog.tags[0] : 'No tags'} • {new Date(blog.publishedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleHomepageBlogToggle(blog.id)}
                          disabled={selectedHomepageBlogs.length >= 3}
                          className={selectedHomepageBlogs.length >= 3 ? 'opacity-50' : ''}
                        >
                          {selectedHomepageBlogs.length >= 3 ? 'Max Reached' : 'Add'}
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Beta Requests & Discovery Calls */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Beta Requests & Discovery Calls
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Request Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center">
                    <Users className="w-6 h-6 text-blue-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-slate-600">Total Requests</p>
                      <p className="text-xl font-bold text-slate-900">{requests.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center">
                    <Calendar className="w-6 h-6 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-slate-600">Beta Requests</p>
                      <p className="text-xl font-bold text-slate-900">
                        {requests.filter(r => r.type === 'beta').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center">
                    <Calendar className="w-6 h-6 text-purple-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-slate-600">Discovery Calls</p>
                      <p className="text-xl font-bold text-slate-900">
                        {requests.filter(r => r.type === 'discovery').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>


            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportData}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
              >
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Analytics & Metrics */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Site Analytics & Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Page Views */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Page Views</p>
                      <p className="text-2xl font-bold text-slate-900">12,543</p>
                      <p className="text-sm text-green-600 flex items-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        +12% from last month
                      </p>
                    </div>
                    <Activity className="w-8 h-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              {/* Unique Visitors */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Unique Visitors</p>
                      <p className="text-2xl font-bold text-slate-900">3,247</p>
                      <p className="text-sm text-green-600 flex items-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        +8% from last month
                      </p>
                    </div>
                    <Users className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              {/* Blog Engagement */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Blog Engagement</p>
                      <p className="text-2xl font-bold text-slate-900">4.2 min</p>
                      <p className="text-sm text-green-600 flex items-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        +15% avg. read time
                      </p>
                    </div>
                    <FileText className="w-8 h-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Additional Metrics */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Top Pages</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">/blog</span>
                      <span className="text-sm font-medium">2,341 views</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">/</span>
                      <span className="text-sm font-medium">1,892 views</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">/blog/ai-financial-analysis</span>
                      <span className="text-sm font-medium">743 views</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Traffic Sources</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Direct</span>
                      <span className="text-sm font-medium">45%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Search Engines</span>
                      <span className="text-sm font-medium">32%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Social Media</span>
                      <span className="text-sm font-medium">23%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default AdminPage;
